import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, LogOut, ChevronDown, Loader2, FileDown, Inbox, Clock,
  SearchCheck, CheckCircle2, XCircle, AlertCircle, Users, ClipboardList, ShieldCheck, FolderOpen, Search, Printer, X, Trash2, BellRing, Landmark,
} from "lucide-react";
import { ASSETS } from "../mock";
import AdminAuthFlow from "./admin/AdminAuthFlow";
import AdminUsers from "./admin/AdminUsers";
import ClientFile from "./admin/crm/ClientFile";
import RejectDialog from "./admin/RejectDialog";
import TransactionsReport from "./admin/crm/TransactionsReport";
import AdminCashouts from "./admin/AdminCashouts";
import AdminDevices from "./admin/AdminDevices";
import TwoFactorSettings from "./admin/TwoFactorSettings";
import ChangePasswordCard from "./admin/ChangePasswordCard";
import { exportApplicationsPdf, exportApplicationPdf } from "./admin/exportPdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  en_revision: { label: "En revisión", cls: "bg-blue-100 text-blue-700 border-blue-200", icon: SearchCheck },
  aprobado: { label: "Aprobado", cls: "bg-[#0fe814]/15 text-[#0fb013] border-[#0fe814]/30", icon: CheckCircle2 },
  rechazado: { label: "Rechazado", cls: "bg-[#ff603e]/10 text-[#ff603e] border-[#ff603e]/25", icon: XCircle },
};

const DOC_LABELS = {
  aviso_operaciones: "Aviso de Operaciones",
  registro_publico: "Registro Público",
  cedula_pasaporte: "Cédula o Pasaporte",
  recibo_servicio: "Recibo de Agua o Luz",
  carta_bancaria: "Carta de Certificación Bancaria",
};

const METHOD_LABELS = {
  tarjetas: "Tarjetas Visa/Mastercard",
  yappy: "Yappy",
  ach: "Transferencias ACH",
  suscripciones: "Suscripciones",
};

const ROLE_LABELS = { superadmin: "Superadmin", admin: "Admin", lector: "Lector" };

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-PA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pendiente;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border ${meta.cls}`}>
      <Icon size={12} />
      {meta.label}
    </span>
  );
};

const DetailItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-wider text-black/40">{label}</p>
    <p className="text-[13px] font-semibold text-black break-words">{value || "—"}</p>
  </div>
);

const AdminPanel = () => {
  const [token, setToken] = useState(sessionStorage.getItem("rp_admin_token") || "");
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("rp_admin_user") || "null"); } catch { return null; }
  });
  const [tab, setTab] = useState("solicitudes");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("status");
  const [testCount, setTestCount] = useState(0);
  const [stale, setStale] = useState([]);
  const [toolBusy, setToolBusy] = useState("");
  const [toolMsg, setToolMsg] = useState("");

  const fetchTools = useCallback(async (tk) => {
    const h = { headers: { Authorization: `Bearer ${tk}` } };
    try {
      const [tc, st] = await Promise.all([axios.get(`${API}/crm/test-data`, h).catch(() => ({ data: { count: 0 } })), axios.get(`${API}/crm/alerts/pending`, h)]);
      setTestCount(tc.data.count || 0);
      setStale(st.data.stale || []);
    } catch { /* lector u offline */ }
  }, []);

  const deleteTestData = async () => {
    if (!window.confirm(`¿Eliminar ${testCount} solicitud(es) de prueba (TEST_* / @example.com) con sus transacciones, notas y documentos? Esta acción no se puede deshacer.`)) return;
    setToolBusy("clean");
    try {
      const res = await axios.delete(`${API}/crm/test-data`, { headers });
      setToolMsg(`${res.data.deleted} solicitud(es) de prueba eliminada(s)`);
      await fetchApps(token);
      await fetchTools(token);
    } catch (err) {
      setToolMsg(err.response?.data?.detail || "No se pudo limpiar");
    } finally {
      setToolBusy("");
    }
  };

  const sendStaleAlert = async () => {
    setToolBusy("alert");
    try {
      const res = await axios.post(`${API}/crm/alerts/pending/send`, {}, { headers });
      setToolMsg(res.data.sent ? `Alerta enviada al equipo con ${res.data.alerted} solicitud(es)` : "No hay pendientes >48h o el correo no está configurado");
      await fetchTools(token);
    } finally {
      setToolBusy("");
    }
  };

  const headers = { Authorization: `Bearer ${token}` };
  const canManage = user && ["superadmin", "admin"].includes(user.role);

  const logout = useCallback(() => {
    setToken("");
    setUser(null);
    setApps([]);
    sessionStorage.removeItem("rp_admin_token");
    sessionStorage.removeItem("rp_admin_user");
  }, []);

  const handle401 = (err) => {
    if (err.response?.status === 401) logout();
  };

  const fetchApps = useCallback(async (tk) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/onboarding`, { headers: { Authorization: `Bearer ${tk}` } });
      setApps(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        setError("Error cargando solicitudes. Intenta de nuevo");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) { fetchApps(token); fetchTools(token); }
  }, [token, fetchApps, fetchTools]);

  const onAuthenticated = (tk, usr) => {
    sessionStorage.setItem("rp_admin_token", tk);
    sessionStorage.setItem("rp_admin_user", JSON.stringify(usr));
    setToken(tk);
    setUser(usr);
    setTab("solicitudes");
  };

  const changeStatus = async (appId, status, reason = "") => {
    if (status === "rechazado" && !reason) {
      setRejecting(apps.find((a) => a.id === appId) || null);
      return;
    }
    setUpdating(appId + status);
    setStatusError("");
    try {
      const res = await axios.patch(`${API}/onboarding/${appId}/status`, { status, reason }, { headers });
      setApps((list) => list.map((a) => (a.id === appId ? res.data : a)));
      setRejecting(null);
    } catch (err) {
      handle401(err);
      const d = err.response?.data?.detail;
      setStatusError(typeof d === "string" ? d : "No se pudo cambiar el estado");
    } finally {
      setUpdating(null);
    }
  };

  const downloadDoc = async (doc) => {
    setDownloading(doc.file_id);
    try {
      const res = await axios.get(`${API}/onboarding/file/${doc.file_id}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.file_id;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      handle401(err);
    } finally {
      setDownloading(null);
    }
  };

  /* ---------- No autenticado: flujo de login + 2FA ---------- */
  if (!token || !user) {
    return <AdminAuthFlow onAuthenticated={onAuthenticated} />;
  }

  /* ---------- Dashboard ---------- */
  const stats = {
    total: apps.length,
    pendiente: apps.filter((a) => a.status === "pendiente").length,
    en_revision: apps.filter((a) => a.status === "en_revision").length,
    aprobado: apps.filter((a) => a.status === "aprobado").length,
    rechazado: apps.filter((a) => a.status === "rechazado").length,
  };

  const q = search.trim().toLowerCase();
  const STATUS_ORDER = { pendiente: 0, en_revision: 1, aprobado: 2, rechazado: 3 };
  const filteredApps = apps
    .filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!q) return true;
      return [a.reference, a.business?.name, a.business?.ruc, a.representative?.full_name, a.representative?.email]
        .some((v) => (v || "").toLowerCase().includes(q));
    })
    .sort((a, b) => {
      const byDateDesc = new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === "status") return (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || byDateDesc;
      if (sortBy === "oldest") return -byDateDesc;
      if (sortBy === "name") return (a.business?.name || "").localeCompare(b.business?.name || "", "es");
      return byDateDesc;
    });

  return (
    <div className="font-montserrat min-h-screen bg-[#fafcf9]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[64px] gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img src={ASSETS.logo} alt="RapidPay" className="h-7 sm:h-8 w-auto shrink-0" />
            <span className="hidden sm:inline text-[12px] font-bold uppercase tracking-wider text-black/40 border-l border-black/10 pl-3">
              Panel Administrativo
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex flex-col items-end mr-1">
              <span className="text-[12px] font-bold text-black leading-tight">{user.name || user.email}</span>
              <span className="text-[10px] font-bold text-[#0fb013] uppercase tracking-wide">{ROLE_LABELS[user.role] || user.role}</span>
            </div>
            <button
              data-testid="admin-refresh"
              onClick={() => fetchApps(token)}
              className="w-9 h-9 rounded-full bg-black/[0.04] hover:bg-black/[0.09] flex items-center justify-center transition-colors"
              title="Actualizar"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-[12px] font-bold text-black px-4 py-2 rounded-full border-2 border-black/15 hover:border-black transition-colors"
            >
              <LogOut size={13} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1">
          <button
            data-testid="tab-solicitudes"
            onClick={() => setTab("solicitudes")}
            className={`inline-flex items-center gap-2 text-[13px] font-bold px-4 py-3 border-b-[3px] transition-colors ${tab === "solicitudes" ? "border-[#0fe814] text-black" : "border-transparent text-black/45 hover:text-black"}`}
          >
            <ClipboardList size={15} /> Solicitudes
          </button>
          <button
            data-testid="tab-cashouts"
            onClick={() => setTab("cashouts")}
            className={`inline-flex items-center gap-2 text-[13px] font-bold px-4 py-3 border-b-[3px] transition-colors ${tab === "cashouts" ? "border-[#0fe814] text-black" : "border-transparent text-black/45 hover:text-black"}`}
          >
            <Landmark size={15} /> Cashouts
          </button>
          <button
            data-testid="tab-seguridad"
            onClick={() => setTab("seguridad")}
            className={`inline-flex items-center gap-2 text-[13px] font-bold px-4 py-3 border-b-[3px] transition-colors ${tab === "seguridad" ? "border-[#0fe814] text-black" : "border-transparent text-black/45 hover:text-black"}`}
          >
            <ShieldCheck size={15} /> Seguridad
          </button>
          {user.role === "superadmin" && (
            <button
              data-testid="tab-usuarios"
              onClick={() => setTab("usuarios")}
              className={`inline-flex items-center gap-2 text-[13px] font-bold px-4 py-3 border-b-[3px] transition-colors ${tab === "usuarios" ? "border-[#0fe814] text-black" : "border-transparent text-black/45 hover:text-black"}`}
            >
              <Users size={15} /> Usuarios
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {tab === "seguridad" ? (
          <>
            <ChangePasswordCard headers={headers} />
            <TwoFactorSettings headers={headers} />
            <AdminDevices headers={headers} />
          </>
        ) : tab === "cashouts" ? (
          <AdminCashouts headers={headers} user={user} apps={apps} />
        ) : tab === "usuarios" && user.role === "superadmin" ? (
          <AdminUsers token={token} currentUser={user} />
        ) : clientId ? (
          <ClientFile
            appId={clientId}
            headers={headers}
            canManage={canManage}
            StatusBadge={StatusBadge}
            onBack={() => { setClientId(null); fetchApps(token); }}
            onAppUpdated={(updated) => setApps((list) => list.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)))}
          />
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              {[
                { label: "Total", value: stats.total, icon: Inbox, cls: "text-black" },
                { label: "Pendientes", value: stats.pendiente, icon: Clock, cls: "text-amber-600" },
                { label: "En revisión", value: stats.en_revision, icon: SearchCheck, cls: "text-blue-600" },
                { label: "Aprobados", value: stats.aprobado, icon: CheckCircle2, cls: "text-[#0fb013]" },
                { label: "Rechazados", value: stats.rechazado, icon: XCircle, cls: "text-[#ff603e]" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-white border border-black/[0.06] rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-black/40">{s.label}</span>
                      <Icon size={15} className={s.cls} />
                    </div>
                    <div className={`mt-1 font-extrabold text-[26px] ${s.cls}`}>{s.value}</div>
                  </div>
                );
              })}
            </div>

            {/* Metrics by client type */}
            {apps.length > 0 && (
              <div data-testid="admin-category-metrics" className="bg-white border border-black/[0.06] rounded-2xl p-4 sm:p-5 mb-6">
                <p className="text-[11px] font-bold uppercase tracking-wider text-black/40 mb-3">Solicitudes por tipo de cliente</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(
                    apps.reduce((acc, a) => {
                      const cat = a.business?.category || "Sin categoría";
                      acc[cat] = (acc[cat] || 0) + 1;
                      return acc;
                    }, {})
                  )
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, count]) => (
                      <span
                        key={cat}
                        data-testid={`admin-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-2 text-[12px] font-bold text-black bg-[#fafcf9] border border-black/[0.08] px-3.5 py-2 rounded-full"
                      >
                        {cat}
                        <span className="text-[11px] font-extrabold text-[#0fb013] bg-[#0fe814]/15 px-2 py-0.5 rounded-full">{count}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}

            {(error || statusError) && (
              <p data-testid="admin-error" className="mb-4 text-[12px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-4 py-2.5 rounded-xl inline-flex items-center gap-2">
                <AlertCircle size={14} /> {error || statusError}
              </p>
            )}

            {(stale.length > 0 || testCount > 0 || toolMsg) && (
              <div data-testid="admin-tools" className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-white border border-black/[0.06] rounded-2xl p-3 sm:p-4">
                {stale.length > 0 && (
                  <div className="flex-1 flex items-center gap-2 text-[12px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-3.5 py-2 rounded-xl">
                    <BellRing size={14} className="shrink-0" />
                    <span data-testid="stale-count"><b>{stale.length}</b> solicitud(es) pendiente(s) hace más de 48h sin revisar · el equipo recibe aviso automático cada hora</span>
                    {canManage && (
                      <button data-testid="stale-send" onClick={sendStaleAlert} disabled={toolBusy === "alert"} className="ml-auto shrink-0 text-[11px] font-bold text-black bg-amber-300 hover:bg-amber-400 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
                        {toolBusy === "alert" ? <Loader2 size={12} className="animate-spin" /> : "Reenviar alerta ahora"}
                      </button>
                    )}
                  </div>
                )}
                {testCount > 0 && user.role === "superadmin" && (
                  <button data-testid="clean-test-data" onClick={deleteTestData} disabled={toolBusy === "clean"}
                    className="inline-flex items-center justify-center gap-1.5 text-[12px] font-bold text-[#ff603e] bg-[#ff603e]/10 hover:bg-[#ff603e]/20 px-4 py-2.5 rounded-full transition-colors disabled:opacity-50">
                    {toolBusy === "clean" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Limpiar {testCount} solicitud(es) de prueba
                  </button>
                )}
                {toolMsg && <span data-testid="tool-msg" className="text-[12px] font-bold text-[#0fb013]">{toolMsg}</span>}
              </div>
            )}

            <TransactionsReport headers={headers} />

            {/* Search & filters */}
            <div data-testid="apps-toolbar" className="bg-white border border-black/[0.06] rounded-2xl p-3 sm:p-4 mb-4 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35" />
                <input
                  data-testid="apps-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, RUC, referencia, representante o correo…"
                  className="w-full pl-10 pr-9 py-2.5 text-[13px] font-semibold text-black bg-[#fafcf9] border-2 border-black/10 rounded-full focus:border-[#0fb013] focus:outline-none transition-colors"
                />
                {search && <button data-testid="apps-search-clear" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black"><X size={14} /></button>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {[["all", "Todos"], ...Object.entries(STATUS_META).map(([k, m]) => [k, m.label])].map(([k, l]) => (
                  <button key={k} data-testid={`apps-filter-${k}`} onClick={() => setStatusFilter(k)}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${statusFilter === k ? "bg-black text-white border-black" : "border-black/10 text-black/55 hover:border-black/40"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <select
                data-testid="apps-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-[12px] font-bold text-black bg-white border-2 border-black/10 rounded-full px-3.5 py-2 focus:border-[#0fb013] focus:outline-none"
                title="Ordenar por"
              >
                <option value="status">Orden: Estado</option>
                <option value="newest">Orden: Más recientes</option>
                <option value="oldest">Orden: Más antiguos</option>
                <option value="name">Orden: Nombre A–Z</option>
              </select>
              <button
                data-testid="apps-export-pdf"
                onClick={() => exportApplicationsPdf(filteredApps, { search: search.trim(), status: statusFilter })}
                disabled={filteredApps.length === 0}
                className="inline-flex items-center justify-center gap-1.5 text-[12px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-4 py-2.5 rounded-full transition-colors disabled:opacity-50"
              >
                <Printer size={14} /> Exportar PDF ({filteredApps.length})
              </button>
            </div>

            {/* Applications list */}
            {loading && apps.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={28} className="animate-spin text-black/30" />
              </div>
            ) : apps.length === 0 ? (
              <div className="bg-white border border-black/[0.06] rounded-2xl p-12 text-center">
                <Inbox size={36} className="mx-auto text-black/20" />
                <p className="mt-3 text-[14px] font-bold text-black/50">Aún no hay solicitudes de registro</p>
              </div>
            ) : filteredApps.length === 0 ? (
              <div data-testid="apps-empty-filter" className="bg-white border border-black/[0.06] rounded-2xl p-12 text-center">
                <Search size={36} className="mx-auto text-black/20" />
                <p className="mt-3 text-[14px] font-bold text-black/50">Ninguna solicitud coincide con la búsqueda o el filtro</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredApps.map((app) => {
                  const isOpen = expanded === app.id;
                  return (
                    <div key={app.id} className="bg-white border border-black/[0.06] rounded-2xl overflow-hidden">
                      {/* Row header */}
                      <button
                        data-testid={`admin-app-toggle-${app.reference}`}
                        onClick={() => setExpanded(isOpen ? null : app.id)}
                        className="w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-5 py-4 text-left hover:bg-black/[0.015] transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="shrink-0 font-extrabold text-[12px] text-[#0fb013] bg-[#0fe814]/10 px-2.5 py-1 rounded-lg">
                            {app.reference}
                          </span>
                          <div className="min-w-0">
                            <p className="text-[14px] font-bold text-black truncate">{app.business?.name}</p>
                            <p className="text-[11px] font-medium text-black/45 truncate">
                              {app.business?.category} · {fmtDate(app.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <StatusBadge status={app.status} />
                          <ChevronDown size={16} className={`text-black/40 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                        </div>
                      </button>

                      {/* Expanded detail */}
                      {isOpen && (
                        <div className="border-t border-black/[0.05] px-4 sm:px-5 py-5 space-y-5 bg-[#fafcf9]/60">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <DetailItem label="RUC" value={app.business?.ruc} />
                            <DetailItem label="Tipo de cliente" value={app.business?.category} />
                            <DetailItem label="Sitio web" value={app.business?.website} />
                            <DetailItem label="Idioma" value={(app.language || "es").toUpperCase()} />
                            <DetailItem label="Representante" value={app.representative?.full_name} />
                            <DetailItem label="Cédula / Pasaporte" value={app.representative?.doc_id} />
                            <DetailItem label="Nacionalidad" value={app.representative?.nationality} />
                            <DetailItem label="Correo" value={app.representative?.email} />
                            <DetailItem label="Teléfono" value={app.representative?.phone} />
                            <DetailItem label="Métodos" value={(app.payment?.methods || []).map((m) => METHOD_LABELS[m] || m).join(", ")} />
                            <DetailItem label="Volumen mensual" value={app.payment?.monthly_volume} />
                            <DetailItem label="Banco de Banco (CashOut)" value={app.payment?.bank} />
                            <DetailItem label="Número de cuenta" value={app.payment?.account_number} />
                            <DetailItem label="Tipo de cuenta" value={{ ahorro: "Cuenta de Ahorro", corriente: "Cuenta Corriente" }[app.payment?.account_type] || app.payment?.account_type} />
                          </div>

                          {app.status === "rechazado" && app.rejection_reason && (
                            <p data-testid="admin-rejection-reason" className="text-[12px] font-semibold text-[#ff603e] bg-[#ff603e]/10 px-3.5 py-2.5 rounded-xl"><b>Motivo de rechazo:</b> {app.rejection_reason}</p>
                          )}

                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              data-testid={`admin-open-crm-${app.reference}`}
                              onClick={() => setClientId(app.id)}
                              className="inline-flex items-center gap-2 text-[12px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-4 py-2.5 rounded-full transition-colors shadow-[0_4px_12px_rgba(15,232,20,0.3)]"
                            >
                              <FolderOpen size={14} /> Abrir ficha CRM (transacciones, bitácora, KYC)
                            </button>
                            <button
                              data-testid={`admin-export-pdf-${app.reference}`}
                              onClick={() => exportApplicationPdf(app)}
                              className="inline-flex items-center gap-2 text-[12px] font-bold text-black/70 hover:text-black bg-black/[0.05] hover:bg-black/10 px-4 py-2.5 rounded-full transition-colors"
                            >
                              <Printer size={14} /> Exportar esta solicitud a PDF
                            </button>
                          </div>

                          {/* Documents */}
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-2">Documentos</p>
                            <div className="flex flex-wrap gap-2">
                              {(app.documents || []).map((doc) => (
                                <button
                                  key={doc.file_id}
                                  data-testid={`admin-doc-${doc.doc_type}`}
                                  onClick={() => canManage && downloadDoc(doc)}
                                  disabled={!canManage || downloading === doc.file_id}
                                  title={canManage ? "Descargar" : "Solo los roles Admin pueden descargar"}
                                  className={`inline-flex items-center gap-2 text-[11px] font-bold px-3 py-2 rounded-xl border transition-colors ${canManage ? "text-black bg-white border-black/10 hover:border-[#0fb013] hover:text-[#0fb013]" : "text-black/35 bg-black/[0.03] border-black/[0.06] cursor-not-allowed"}`}
                                >
                                  {downloading === doc.file_id ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                                  {DOC_LABELS[doc.doc_type] || doc.doc_type}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Status actions */}
                          {canManage && (
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-2">Cambiar estado</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(STATUS_META).map(([key, meta]) => (
                                  <button
                                    key={key}
                                    data-testid={`admin-status-${key}`}
                                    onClick={() => changeStatus(app.id, key)}
                                    disabled={app.status === key || updating === app.id + key}
                                    className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3.5 py-2 rounded-full border-2 transition-colors ${app.status === key ? `${meta.cls} cursor-default` : "border-black/10 text-black/55 bg-white hover:border-black/40 hover:text-black"}`}
                                  >
                                    {updating === app.id + key ? <Loader2 size={12} className="animate-spin" /> : React.createElement(meta.icon, { size: 12 })}
                                    {meta.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-8 flex items-center gap-2 text-black/35">
              <ShieldCheck size={14} className="text-[#0fb013]" />
              <span className="text-[11px] font-medium">
                Sesión protegida con 2FA · Rol: {ROLE_LABELS[user.role] || user.role}
              </span>
            </div>
          </>
        )}
      </main>

      <RejectDialog
        open={Boolean(rejecting)}
        app={rejecting}
        submitting={Boolean(rejecting && updating === rejecting.id + "rechazado")}
        onCancel={() => setRejecting(null)}
        onConfirm={(reason) => changeStatus(rejecting.id, "rechazado", reason)}
      />
    </div>
  );
};

export default AdminPanel;
